/**
 * FEAT-010 — Dexie-backed workspace-wide vehicle profile library.
 *
 * Unlike most repositories in this layer, `vehicleProfiles` is NOT scoped per
 * station — the same profile is shared across every station the user opens.
 * Station deletion therefore does NOT cascade into this table; profiles only
 * disappear via explicit user action in VehiclesPage (Session 8f.9).
 *
 * Primary key is `id` (UUID v4, branded as VehicleProfileId).
 */
import type { VehicleProfileSnapshot } from '@/domain';
import type { VehicleProfileId } from '@/domain/types';
import type { VehicleRepository } from '@/persistence/ports';
import type { Evse3Database, VehicleProfileRow } from '../db';

export class VehicleRepositoryDexie implements VehicleRepository {
  constructor(private readonly db: Evse3Database) {}

  async list(): Promise<readonly VehicleProfileSnapshot[]> {
    const rows = await this.db.vehicleProfiles.toArray();
    return rows.map((r) => toSnapshot(r));
  }

  async get(id: VehicleProfileId): Promise<VehicleProfileSnapshot | null> {
    const row = await this.db.vehicleProfiles.get(id);
    return row ? toSnapshot(row) : null;
  }

  async upsert(snap: VehicleProfileSnapshot): Promise<void> {
    const row: VehicleProfileRow = {
      id: snap.id,
      name: snap.name,
      batteryKwh: snap.batteryKwh,
      maxAcPowerKw: snap.maxAcPowerKw,
      maxDcPowerKw: snap.maxDcPowerKw,
      defaultStartSocPct: snap.defaultStartSocPct,
      ...(snap.chargingCurve !== undefined
        ? { chargingCurve: snap.chargingCurve.map((p) => ({ socPct: p.socPct, powerKw: p.powerKw })) }
        : {}),
      isPreset: snap.isPreset,
      createdAt: snap.createdAt,
    };
    await this.db.vehicleProfiles.put(row);
  }

  async delete(id: VehicleProfileId): Promise<void> {
    await this.db.vehicleProfiles.delete(id);
  }
}

function toSnapshot(r: VehicleProfileRow): VehicleProfileSnapshot {
  return {
    id: r.id,
    name: r.name,
    batteryKwh: r.batteryKwh,
    maxAcPowerKw: r.maxAcPowerKw,
    maxDcPowerKw: r.maxDcPowerKw,
    defaultStartSocPct: r.defaultStartSocPct,
    ...(r.chargingCurve !== undefined
      ? { chargingCurve: r.chargingCurve.map((p) => ({ socPct: p.socPct, powerKw: p.powerKw })) }
      : {}),
    isPreset: r.isPreset,
    createdAt: r.createdAt,
  };
}
